import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowDown, ArrowLeft, ArrowUp, Image as ImageIcon, Loader2, Trash2, Upload } from 'lucide-react';
import PageHeader from '../../components/shared/PageHeader';
import { Card } from '../../components/shared/DataToolbar';
import { LoadingState, ErrorState } from '../../components/shared/Pending';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { useApi } from '../../api/useApi';
import { deletePropertyImage, getProperty, listPropertyImages, reorderPropertyImages, setPropertyImageCover, uploadPropertyImages } from '../../api/hostApi';
import { toast } from 'sonner';

export default function PropertyImages() {
  const { id } = useParams();
  const { data: property } = useApi(() => getProperty(id), [id]);
  const { data, loading, error, refetch } = useApi(() => listPropertyImages(id), [id]);
  const images = useMemo(() => {
    const items = Array.isArray(data) ? data : (data?.items || []);
    return [...items].sort((a, b) => Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0));
  }, [data]);
  const [file, setFile] = useState(null);
  const [alt, setAlt] = useState('');
  const [asCover, setAsCover] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reordering, setReordering] = useState(false);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      if (alt.trim()) fd.append('alt_text', alt.trim());
      if (asCover) fd.append('set_as_cover', '1');
      await uploadPropertyImages(id, fd);
      setFile(null);
      setAlt('');
      setAsCover(false);
      toast.success('Image uploaded');
      refetch();
    } catch (e) {
      toast.error(e?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const move = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= images.length || reordering) return;
    const next = images.map((image) => image.id);
    [next[index], next[target]] = [next[target], next[index]];
    setReordering(true);
    try {
      await reorderPropertyImages(id, next);
      toast.success('Image order updated');
      await refetch();
    } catch (e) {
      toast.error(e?.message || 'Could not reorder images');
    } finally {
      setReordering(false);
    }
  };

  const setCover = async (imageId) => {
    try {
      await setPropertyImageCover(id, imageId);
      toast.success('Cover image updated');
      refetch();
    } catch (e) {
      toast.error(e?.message || 'Could not update cover image');
    }
  };

  const remove = async (imageId) => {
    try {
      await deletePropertyImage(id, imageId);
      toast.success('Image removed');
      refetch();
    } catch (e) {
      toast.error(e?.message || 'Could not remove image');
    }
  };

  return (
    <div>
      <PageHeader
        breadcrumbs={['Properties', property?.title || `#${id}`, 'Images']}
        title="Property images"
        description="Upload, select the cover image, and control the exact gallery order."
        actions={<Button asChild variant="outline"><Link to={`/host/properties/${id}`}><ArrowLeft className="mr-1.5 h-4 w-4" /> Back to property</Link></Button>}
      />

      <Card className="p-6">
        <h3 className="font-semibold text-slate-900">Upload image</h3>
        <div className="mt-4 grid items-end gap-3 md:grid-cols-[1fr_1fr_auto]">
          <div><Label>Image file</Label><Input type="file" accept="image/*" onChange={(event) => setFile(event.target.files?.[0] || null)} className="mt-1.5" /></div>
          <div><Label>Alt text</Label><Input value={alt} onChange={(event) => setAlt(event.target.value)} className="mt-1.5" placeholder="Describe the image" /></div>
          <Button onClick={upload} disabled={!file || uploading} className="h-10 bg-teal-700 hover:bg-teal-800">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="mr-1.5 h-4 w-4" /> Upload</>}</Button>
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm text-slate-600"><Checkbox checked={asCover} onCheckedChange={setAsCover} /> Set new image as cover</label>
      </Card>

      <div className="mt-5">
        {loading ? <LoadingState label="Loading images…" /> : error ? <ErrorState error={error} onRetry={refetch} /> : images.length === 0 ? (
          <Card className="p-14 text-center"><ImageIcon className="mx-auto h-8 w-8 text-slate-300" /><div className="mt-3 font-semibold text-slate-900">No property images</div><div className="mt-1 text-sm text-slate-500">Upload at least one image before publishing the listing.</div></Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {images.map((image, index) => {
              const cover = image.is_cover || image.isCover;
              return (
                <Card key={image.id} className="overflow-hidden">
                  <div className="relative aspect-[4/3] bg-slate-100"><img src={image.url} alt={image.alt_text || ''} className="h-full w-full object-cover" />{cover && <span className="absolute left-3 top-3 rounded-full bg-teal-700 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">Cover</span>}<span className="absolute right-3 top-3 rounded-full bg-slate-950/70 px-2 py-1 text-[10px] font-semibold text-white">#{index + 1}</span></div>
                  <div className="p-3">
                    <div className="flex items-center gap-2">
                      <Button type="button" variant="outline" size="sm" disabled={index === 0 || reordering} onClick={() => move(index, -1)} title="Move earlier"><ArrowUp className="h-3.5 w-3.5" /></Button>
                      <Button type="button" variant="outline" size="sm" disabled={index === images.length - 1 || reordering} onClick={() => move(index, 1)} title="Move later"><ArrowDown className="h-3.5 w-3.5" /></Button>
                      {!cover && <Button type="button" variant="outline" size="sm" onClick={() => setCover(image.id)} className="ml-auto text-teal-700">Set cover</Button>}
                      <Button type="button" variant="ghost" size="sm" onClick={() => remove(image.id)} className="text-rose-600"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                    {image.alt_text && <div className="mt-2 truncate text-xs text-slate-500">{image.alt_text}</div>}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
